import $ from 'jquery'
import _ from 'underscore'
import {ReportGroupingLevel} from './constants'
import {escapeHtmlSymbols, stringComparatorCaseSensitive} from '../atUtils'
import messageResource from '../messageResource'
import UserNameFormat from '../util/UserNameFormat'
import {DEFAULT_TIME_ZONE_GROUP_ID, OTHER_USER_GROUPS_ENTITY_ID, OTHER_USERS_ENTITY_ID} from 'constants'
import {DateFormat} from '../date_format'
import {insertBrakes} from '../util/StringUtils'

export default class ReportChartUtils {

    static getChartColorSchemeById(colorId) {
        const schemas = {
            DEFAULT: {
                singleColumn: '#5f94c5',
                notAssigned: '#dedede',
                userTeam: '#4986bf',
                user: '#2e6699',
            },
            AZUREBLUE: {
                singleColumn: '#7facd7',
                notAssigned: '#dedede',
                userTeam: '#6193c2',
                user: '#2e6699',
            },
            AQUAMARINE: {
                singleColumn: '#4ebdd4',
                notAssigned: '#dedede',
                userTeam: '#42a6b9',
                user: '#1f8599',
            },
            FERNGREEN: {
                singleColumn: '#6dbb7a',
                notAssigned: '#dedede',
                userTeam: '#55ab5f',
                user: '#2e993b',
            },
            YELLOWGREEN: {
                singleColumn: '#afd05b',
                notAssigned: '#dedede',
                userTeam: '#99bc41',
                user: '#94991f',
            },
            CHARCOALGREY: {
                singleColumn: '#95a9ba',
                notAssigned: '#dedede',
                userTeam: '#7b96ab',
                user: '#54748c',
            },
            PURPLE: {
                singleColumn: '#995fbf',
                notAssigned: '#dedede',
                userTeam: '#8c54b2',
                user: '#681f99',
            },
            ORANGE: {
                singleColumn: '#e2b145',
                notAssigned: '#dedede',
                userTeam: '#d59d30',
                user: '#99701f',
            },
            RED: {
                singleColumn: '#ca575b',
                notAssigned: '#dedede',
                userTeam: '#c13848',
                user: '#991f2d',
            },
        }
        return schemas[colorId]
    }

    static getSortCategoryFunctionForChart(groupingLevel) {
        switch (groupingLevel) {
            case ReportGroupingLevel.USER:
                return (user1, user2) => {
                    const res = stringComparatorCaseSensitive(user1.lastName, user2.lastName)
                    return res === 0 ? stringComparatorCaseSensitive(user1.firstName, user2.firstName) : res
                }
            case ReportGroupingLevel.USER_GROUP: {
                return (item1, item2) => {
                    if (item1.id === item2.id) return 0
                    if (item1.id === OTHER_USER_GROUPS_ENTITY_ID) return -1
                    if (item2.id === OTHER_USER_GROUPS_ENTITY_ID) return 1
                    if (item1.id === window.SystemSettings.usersWithoutGroupId) return -1
                    if (item2.id === window.SystemSettings.usersWithoutGroupId) return 1

                    return stringComparatorCaseSensitive(item1.name, item2.name)
                }
            }
            case ReportGroupingLevel.TIME_ZONE_GROUP:
                return (item1, item2) => {
                    if (item1.id === item2.id) return 0
                    if (item1.id === OTHER_USER_GROUPS_ENTITY_ID) return -1
                    if (item2.id === OTHER_USER_GROUPS_ENTITY_ID) return 1
                    if (item1.id === DEFAULT_TIME_ZONE_GROUP_ID) return -1
                    if (item2.id === DEFAULT_TIME_ZONE_GROUP_ID) return 1

                    return stringComparatorCaseSensitive(item1.name, item2.name)
                }
            case ReportGroupingLevel.LEAVE_TYPE:
                return (item1, item2) => {
                    return stringComparatorCaseSensitive(item1.name, item2.name)
                }
            case ReportGroupingLevel.CUSTOMER:
            case ReportGroupingLevel.PROJECT:
            case ReportGroupingLevel.BILLING_TYPE:
                return (item1, item2) => {
                    return stringComparatorCaseSensitive(item1.name, item2.name)
                }
            case ReportGroupingLevel.DATE:
                return (date1, date2) => {
                    const jsDate1 = Date.parseJSONDate(date1)
                    const jsDate2 = Date.parseJSONDate(date2)
                    return jsDate1.getTime() > jsDate2.getTime() ? 1 : -1
                }
            case ReportGroupingLevel.WEEK:
                return (week1, week2) => {
                    const jsDate1 = Date.parseJSONDate(week1.from)
                    const jsDate2 = Date.parseJSONDate(week2.from)
                    return jsDate1.getTime() > jsDate2.getTime() ? 1 : -1
                }
            case ReportGroupingLevel.MONTH:
                return (month1, month2) => {
                    const jsDate1 = Date.parseJSONDate(month1)
                    const jsDate2 = Date.parseJSONDate(month2)
                    return jsDate1.getTime() > jsDate2.getTime() ? 1 : -1
                }
        }
    }

    static getFormatCategoryFunctionForChart(selectedCategoryType, dateFormat, escape, partsLimit, recursively) {
        if (!partsLimit)
            partsLimit = 17

        switch (selectedCategoryType) {
            case ReportGroupingLevel.USER:
                return (user) => {
                    if ($.isEmptyObject(user)) return ""

                    if (user.id === OTHER_USERS_ENTITY_ID)
                        return messageResource.getMessage("report.other_users")
                    const formattedName = UserNameFormat.formatLFM(user.firstName, user.lastName, user.middleName)
                    if (formattedName.length < partsLimit)
                        return escape ? escapeHtmlSymbols(formattedName) : formattedName
                    let res = ""
                    if (user.lastName.length > partsLimit)
                        res = (escape ? escapeHtmlSymbols(user.lastName.substr(0, partsLimit - 2)) : user.lastName.substr(0, partsLimit - 2)) + '...,<br>'
                    else
                        res = (escape ? escapeHtmlSymbols(user.lastName) : user.lastName) + ",<br>"
                    let firstWithMiddleName = user.firstName + (user.middleName ? " " + user.middleName + "." : "")
                    if (firstWithMiddleName.length > 18)
                        res += (escape ? escapeHtmlSymbols(user.firstName.substr(0, partsLimit - 2)) : user.firstName.substr(0, partsLimit - 2)) + '...'
                    else
                        res += escape ? escapeHtmlSymbols(firstWithMiddleName) : firstWithMiddleName
                    return res
                }
            case ReportGroupingLevel.USER_GROUP: {
                return (item) => {
                    if (item.id === window.SystemSettings.usersWithoutGroupId) {
                        item.name = messageResource.getMessage("common.users_without_group")
                    }
                    if (item.id === OTHER_USER_GROUPS_ENTITY_ID)
                        return messageResource.getMessage("report.create_chart.stacked_columns_popup.not_assigned_users")
                    return !$.isEmptyObject(item) ? insertBrakes(item.name, partsLimit, ' ', partsLimit, "...", escape, recursively) : ""
                }
            }
            case ReportGroupingLevel.TIME_ZONE_GROUP: {
                return (item) => {
                    if (item.id === OTHER_USER_GROUPS_ENTITY_ID)
                        return messageResource.getMessage("report.create_chart.stacked_columns_popup.not_assigned_users")

                    let name = _.result(item, "name", "")
                    if (item.id === DEFAULT_TIME_ZONE_GROUP_ID) {
                        name += " " + messageResource.getMessage("common.default_group")
                    }
                    return !$.isEmptyObject(item) ? insertBrakes(name, partsLimit, ' ', partsLimit, "...", escape, recursively) : ""
                }
            }
            case ReportGroupingLevel.LEAVE_TYPE: {
                return (item) => {
                    return !$.isEmptyObject(item) ? insertBrakes(item.name, partsLimit, ' ', partsLimit, "...", escape, recursively) : ""
                }
            }
            case ReportGroupingLevel.CUSTOMER:
            case ReportGroupingLevel.BILLING_TYPE:
                return (item) => {
                    return !$.isEmptyObject(item) ? insertBrakes(item.name, partsLimit, ' ', partsLimit, "...", escape, recursively) : ""
                }
            case ReportGroupingLevel.PROJECT:
                return (item) => {
                    return !$.isEmptyObject(item) ?
                           insertBrakes(item.name + " (" + item.customerInfo.name + ")",
                               partsLimit,
                               ' ',
                               partsLimit,
                               "...",
                               escape,
                               recursively
                           ) : ""
                }
            case ReportGroupingLevel.DATE:
                return (date) => {
                    if ($.isEmptyObject(date)) return ""
                    return new DateFormat(dateFormat).formatDate(date.parseJSONDate())
                }
            case ReportGroupingLevel.WEEK:
                return (week) => {
                    if ($.isEmptyObject(week)) return ""
                    const from = week.from.parseJSONDate()
                    const to = week.to.parseJSONDate()
                    const firstPart = week.from === week.to ? "" : new DateFormat('MMM dd').formatDate(from) + " - "
                    return firstPart + new DateFormat('MMM dd, yyyy').formatDate(to)
                }
            case ReportGroupingLevel.MONTH:
                return (month) => {
                    if ($.isEmptyObject(month)) return ""
                    return new DateFormat('MMMM , yyyy').formatDate(month.parseJSONDate())
                }
        }
    }
}


// WEBPACK FOOTER //
// ./web/js/reports/ReportChartUtils.js